import { gql, useLazyQuery } from '@apollo/client';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import {
  GeneratedRc,
  GetReasonNameForCandidatesInput,
  GetReasonNameForCandidatesResponse,
  Query,
  Rc,
  UnassignedRc,
} from 'generated/graphql';
import { useCallback } from 'react';
import { useGetUser } from 'v2/core/services/user';

const GET_REASON_NAME_FOR_REASON_CANDIDATES = gql`
  query getReasonNameForCandidates(
    $tenantID: ID!
    $rcs: [String!]!
    $rcList: RCInput
  ) {
    getReasonNameForCandidates(
      request: { tenantID: $tenantID, rcs: $rcs, rcList: $rcList }
    ) {
      displayName
      category
    }
  }
`;

interface GetReasonNameForRCsOptions {
  onCompleted?: (data: GetReasonNameForCandidatesResponse) => void;
  abortSignal?: AbortSignal;
}

export const useGetReasonNameForRCs = ({
  onCompleted,
  abortSignal,
}: GetReasonNameForRCsOptions = {}) => {
  const {
    tenant: { id: tenantID },
  } = useGetUser();
  const { showErrorNotification } = useNotificationContext();

  const [getReasonNameForRCQuery, { data, loading }] = useLazyQuery<
    Query,
    GetReasonNameForCandidatesInput
  >(GET_REASON_NAME_FOR_REASON_CANDIDATES, {
    fetchPolicy: 'cache-and-network',
    context: {
      fetchOptions: {
        signal: abortSignal,
      },
    },
    onError: () => {
      showErrorNotification({
        contentComponent: 'Failed to generate reason name',
        props: {},
      });
    },
    onCompleted: (data) => {
      if (data.getReasonNameForCandidates) {
        onCompleted?.(data.getReasonNameForCandidates);
      }
    },
  });

  const getReasonNameForRC = useCallback(
    async (definitions: Rc[]) => {
      const generatedRCs = definitions.filter(
        (definition): definition is GeneratedRc =>
          definition.__typename === 'GeneratedRC',
      );

      const unassignedRCs = definitions.filter(
        (definition): definition is UnassignedRc =>
          definition.__typename === 'UnassignedRC',
      );

      return await getReasonNameForRCQuery({
        variables: {
          tenantID,
          rcs: [],
          rcList: {
            generatedRCs: generatedRCs.map((rc) => ({
              name: rc.name,
              category: rc.category,
            })),
            unassignedRCs: unassignedRCs.map((rc) => ({
              name: rc.name,
              category: rc.category,
              recordIDs: rc.recordIDs,
            })),
          },
        },
      });
    },
    [getReasonNameForRCQuery, tenantID],
  );

  return [
    getReasonNameForRC,
    {
      data: data?.getReasonNameForCandidates,
      isLoading: loading,
    },
  ] as const;
};
